
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { createClient, getUserApiKey } from '@/lib/supabase-client'
import { type UserProfile, type UsageStats } from '../types'
import Sidebar from './Sidebar'
import DashboardHeader from './DashboardHeader'
import APIKeySection from './APIKeySection'
import UsageStatsSection from './UsageStatsSection'
import BatchUploader from './BatchUploader'

export default function DashboardContent() {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [usage, setUsage] = useState<UsageStats | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const supabase = createClient()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      navigate('/auth/login')
      return
    }

    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .maybeSingle()

    const apiKey = await getUserApiKey(supabase, session.user.id)

    if (profileData) {
      setProfile({
        ...profileData,
        email: session.user.email || '',
        api_key: apiKey || '',
      } as UserProfile)
    } else {
      setProfile({
        id: session.user.id,
        name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
        email: session.user.email || '',
        api_key: apiKey || '',
        subscription_tier: 'free',
      } as UserProfile)
    }

    // Load usage stats
    if (apiKey) {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://eutekssvzhtkwzperkzb.supabase.co/functions/v1'
      // Use /usage for Vercel API, /usage-public for Supabase direct
      const usageEndpoint = apiBaseUrl.includes('supabase.co') ? '/usage-public' : '/usage'
      try {
        const response = await fetch(`${apiBaseUrl}${usageEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ api_key: apiKey }),
        })
        if (response.ok) {
          const usageData = await response.json()
          setUsage(usageData)
        } else {
          await response.json().catch(() => ({ error: 'Unknown error' }))
        }
      } catch (error) {
      }
    }

    setLoading(false)
  }


  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors">
      <Sidebar />

      {/* Main Content */}
      <div className="lg:pl-64">
        <DashboardHeader title="Dashboard" />

        <main className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Section */}
          <div className="mb-8 animate-fade-in">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              Welcome back{profile?.name ? `, ${profile.name.split(' ')[0]}` : ''}! 👋
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Manage your API key, track usage, generate alt text, and convert text to speech
            </p>
          </div>

          {/* Stats Grid */}
          {usage && (
            <div className="mb-8 animate-scale-in" style={{ animationDelay: '0.1s' }}>
              <UsageStatsSection usage={usage} onRefresh={loadData} />
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* API Key Section */}
            {profile && (
              <div className="animate-slide-in" style={{ animationDelay: '0.2s' }}>
                <APIKeySection profile={profile} onUpdate={loadData} />
              </div>
            )}

            {/* Quick Actions */}
            <div className="glass-card rounded-xl p-6 hover-lift animate-scale-in" style={{ animationDelay: '0.3s' }}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Quick Actions
              </h3>
              <div className="space-y-3">
                <Link
                  to="/"
                  className="flex items-center justify-between p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/50 rounded-lg flex items-center justify-center group-hover:bg-primary-200 dark:group-hover:bg-primary-900 transition">
                      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Try Image Generator</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Generate alt text on the homepage</p>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <Link
                  to="/dashboard/tts"
                  className="flex items-center justify-between p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900 transition">
                      <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Text-to-Speech</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Convert text to speech audio</p>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <a
                  href="https://github.com/your-repo/API.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/50 rounded-lg flex items-center justify-center group-hover:bg-primary-200 dark:group-hover:bg-primary-900 transition">
                      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">API Documentation</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">View API reference guide</p>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Batch Uploader */}
          {profile && profile.api_key && (
            <div className="mb-8 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <BatchUploader apiKey={profile.api_key} />
            </div>
          )}

          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                ),
                title: 'API Integration',
                description: 'Use your API key to integrate with WordPress, Shopify, or your custom application.',
                color: 'blue',
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: 'Usage Tracking',
                description: 'Monitor your API usage and stay within your plan limits. Usage resets monthly.',
                color: 'green',
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: 'Upgrade Plan',
                description: 'Need more? Upgrade to Pro or Enterprise for higher limits and priority support.',
                color: 'purple',
              },
            ].map((card, index) => (
              <div
                key={index}
                className={`glass-card rounded-xl p-6 hover-lift animate-scale-in ${
                  card.color === 'blue' ? 'border-blue-200 dark:border-blue-800' :
                  card.color === 'green' ? 'border-green-200 dark:border-green-800' :
                  'border-purple-200 dark:border-purple-800'
                }`}
                style={{ animationDelay: `${0.5 + index * 0.1}s` }}
              >
                <div className={`w-12 h-12 ${
                  card.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                  card.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
                  'bg-purple-100 dark:bg-purple-900/30'
                } rounded-lg flex items-center justify-center mb-4 ${
                  card.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                  card.color === 'green' ? 'text-green-600 dark:text-green-400' :
                  'text-purple-600 dark:text-purple-400'
                }`}>
                  {card.icon}
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{card.title}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">{card.description}</p>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  )
}
